import { environment } from "@ion-caballus/env";

export interface StorageMetric {
    operation: string;
    duration: number;
    timestamp: Date;
    key?: string;
    isCached?: boolean;
}

export interface UniqueOperationStats {
    count: number;
    avgDuration: number;
    cachedCount: number;
    cachedAvgDuration: number;
    uncachedCount: number;
    uncachedAvgDuration: number;
    operations: Record<string, number>;
}

export class StoragePerformanceMonitor {
    private static metrics: StorageMetric[] = [];
    private static readonly MAX_METRICS = 1000;

    static addMetric(metric: StorageMetric): void {
        this.metrics.push(metric);
        if (this.metrics.length > this.MAX_METRICS) {
            this.metrics.shift();
        }
    }

    static getMetrics(): StorageMetric[] {
        return [...this.metrics];
    }

    static clearMetrics(): void {
        this.metrics = [];
    }

    static getAverageTime(operation: string, cached?: boolean): number {
        const relevantMetrics = this.metrics.filter(m => {
            if (cached === undefined) {
                return m.operation === operation;
            }
            return m.operation === operation && m.isCached === cached;
        });
        
        if (relevantMetrics.length === 0) return 0;
        
        const total = relevantMetrics.reduce((sum, metric) => sum + metric.duration, 0);
        return total / relevantMetrics.length;
    }

    static getOperationStats(): Record<string, UniqueOperationStats> {
        return this.metrics.reduce((acc, metric) => {
            const operation = metric.operation;
            
            if (!acc[operation]) {
                acc[operation] = {
                    count: 0,
                    avgDuration: 0,
                    cachedCount: 0,
                    cachedAvgDuration: 0,
                    uncachedCount: 0,
                    uncachedAvgDuration: 0,
                    operations: {}
                };
            }
            
            const stats = acc[operation];
            stats.count++;
            
            // Update cached/uncached stats
            if (metric.isCached) {
                stats.cachedCount++;
                stats.cachedAvgDuration = (
                    (stats.cachedAvgDuration * (stats.cachedCount - 1) + metric.duration) /
                    stats.cachedCount
                );
            } else {
                stats.uncachedCount++;
                stats.uncachedAvgDuration = (
                    (stats.uncachedAvgDuration * (stats.uncachedCount - 1) + metric.duration) /
                    stats.uncachedCount
                );
            }
            
            // Overall average
            stats.avgDuration = (
                (stats.avgDuration * (stats.count - 1) + metric.duration) / 
                stats.count
            );
            
            // Track operations by key if available
            if (metric.key) {
                stats.operations[metric.key] = (stats.operations[metric.key] || 0) + 1;
            }
            
            return acc;
        }, {} as Record<string, UniqueOperationStats>);
    }
}

export function measureStorageOperation() {
    return function (
        target: any,
        propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        if (environment.production) {
            return descriptor;
        }

        const originalMethod = descriptor.value;

        descriptor.value = async function (...args: any[]) {
            const start = performance.now();
            try {
                const result = await originalMethod.apply(this, args);
                const duration = performance.now() - start;
                
                // Get the key if it exists in the arguments
                const key = args[0]?.key || args[0];
                
                // Check if the result came from cache by looking at the internal property
                const isCached = result?.fromCache === true;
                
                StoragePerformanceMonitor.addMetric({
                    operation: propertyKey,
                    duration,
                    timestamp: new Date(),
                    key: typeof key === 'string' ? key : undefined,
                    isCached
                });
                
                return result;
            } catch (error) {
                const duration = performance.now() - start;
                StoragePerformanceMonitor.addMetric({
                    operation: `${propertyKey}_error`,
                    duration,
                    timestamp: new Date()
                });
                throw error;
            }
        };

        return descriptor;
    };
} 